import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { MARKER_COLORS, getFunctionalStatusMeta, normalizeFunctionalStatus } from './InfraShared';
import { getMapTile, hasCartoKey } from '../../../services/mapTileLayers';

const THEME_STORAGE_KEY = 'nlims-infra-map-theme';

function loadStoredTheme() {
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    return stored === 'light' || stored === 'dark' || stored === 'satellite' ? stored : 'light';
  } catch {
    return 'light';
  }
}

// Fix Leaflet default icon path broken by bundlers
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl:       'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl:     'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const NIGERIA_CENTER = [9.5, 8.5];
const DEFAULT_ZOOM   = 6;

// Status-coloured drop-pin DivIcon
function createStatusIcon(status, isSelected = false) {
  const color = MARKER_COLORS[normalizeFunctionalStatus(status)] || '#9ca3af';
  const size  = isSelected ? 46 : 38;
  const html  = `
    <div style="position:relative;width:${size}px;height:${size + 10}px;display:flex;flex-direction:column;align-items:center;
      filter:${isSelected ? `drop-shadow(0 0 8px ${color}99)` : 'drop-shadow(0 2px 4px rgba(0,0,0,0.4))'};transition:transform 0.2s;">
      <div style="width:${size}px;height:${size}px;background:${color};border-radius:50% 50% 50% 0;
        transform:rotate(-45deg);display:flex;align-items:center;justify-content:center;
        border:2.5px solid rgba(255,255,255,0.9);box-shadow:0 2px 8px rgba(0,0,0,0.3);flex-shrink:0;">
        <div style="transform:rotate(45deg);display:flex;align-items:center;justify-content:center;">
          <svg viewBox="0 0 24 24" fill="none" width="16" height="16">
            <path d="M12 2C8.68 2 6 4.68 6 8c0 5.25 6 13 6 13s6-7.75 6-13c0-3.32-2.68-6-6-6z" fill="white" opacity="0.9"/>
            <circle cx="12" cy="8" r="2.5" fill="${color}"/>
          </svg>
        </div>
      </div>
      <div style="width:0;height:0;border-left:5px solid transparent;border-right:5px solid transparent;border-top:10px solid ${color};margin-top:-1px;"></div>
    </div>`;
  return L.divIcon({ html, className: '', iconSize: [size, size + 10], iconAnchor: [size / 2, size + 10] });
}

// Fly to selected marker
function MapController({ selected }) {
  const map = useMap();
  useEffect(() => {
    if (selected) map.flyTo([selected.lat, selected.lng], Math.max(map.getZoom(), 9), { duration: 1.2 });
  }, [selected, map]);
  return null;
}

// Enable/disable all map interactions
function MapInteractionController({ interactive }) {
  const map = useMap();
  useEffect(() => {
    const handlers = [map.dragging, map.scrollWheelZoom, map.doubleClickZoom, map.touchZoom, map.boxZoom];
    handlers.forEach(h => h && (interactive ? h.enable() : h.disable()));
  }, [interactive, map]);
  return null;
}

// Zoom + reset controls
function ZoomButtons() {
  const map = useMap();
  const btn = 'w-8 h-8 bg-white border border-gray-200 rounded-lg flex items-center justify-center cursor-pointer shadow-md hover:bg-gray-50 transition-colors';
  return (
    <div className="absolute bottom-5 right-4 z-[1000] flex flex-col gap-1">
      <button className={btn} title="Reset view" onClick={() => map.flyTo(NIGERIA_CENTER, DEFAULT_ZOOM, { duration: 0.8 })}>
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
          <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" stroke="#374151" strokeWidth="2" />
          <polyline points="9,22 9,12 15,12 15,22" stroke="#374151" strokeWidth="2" />
        </svg>
      </button>
      <button className={btn} title="Zoom in"  onClick={() => map.zoomIn()}>
        <span className="text-gray-700 text-xl font-bold leading-none">+</span>
      </button>
      <button className={btn} title="Zoom out" onClick={() => map.zoomOut()}>
        <span className="text-gray-700 text-xl font-bold leading-none">-</span>
      </button>
    </div>
  );
}

// Light / Dark / Satellite basemap toggle — same control set as the
// Geospatial Overview map (component/Geospatial/Facilitymap.jsx).
function MapThemeControls({ theme, onThemeChange }) {
  const btnBase =
    'w-8 h-8 bg-white border border-gray-200 rounded-lg flex items-center justify-center cursor-pointer shadow-md hover:bg-gray-50 transition-colors';
  const activeBtn = '!bg-green-50 !border-green-400 text-green-700';

  return (
    <div className="absolute top-4 right-4 z-[1000] flex flex-col gap-1">
      <button
        onClick={() => onThemeChange('light')}
        title="Light map"
        className={`${btnBase} ${theme === 'light' ? activeBtn : 'text-gray-600'}`}
      >
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="5" />
          <path d="M12 1v2m0 18v2M4.22 4.22l1.42 1.42m12.72 12.72l1.42 1.42M1 12h2m18 0h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
        </svg>
      </button>
      <button
        onClick={() => onThemeChange('dark')}
        title="Dark map"
        className={`${btnBase} ${theme === 'dark' ? activeBtn : 'text-gray-600'}`}
      >
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" />
        </svg>
      </button>
      <button
        onClick={() => onThemeChange('satellite')}
        title="Satellite view"
        className={`${btnBase} ${theme === 'satellite' ? activeBtn : 'text-gray-600'}`}
      >
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 2a10 10 0 100 20A10 10 0 0012 2z" />
          <path d="M2 12h20M12 2c-2.76 4-4 8-4 10s1.24 6 4 10M12 2c2.76 4 4 8 4 10s-1.24 6-4 10" />
        </svg>
      </button>
    </div>
  );
}

// Facility Map View  Leaflet tiles, status-coloured markers, click-to-interact.
export default function FacilityMap({ markers, selected, onSelect }) {
  const [interactive, setInteractive] = useState(false);
  const [theme, setTheme] = useState(loadStoredTheme);
  const tile = getMapTile(theme);

  const handleThemeChange = (next) => {
    setTheme(next);
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
      // localStorage unavailable (private mode, etc.) — theme just won't persist.
    }
  };

  return (
    <div className="flex flex-col lg:flex-row gap-4">
      {/*  Map container  */}
      <div className="flex-1 relative rounded-2xl overflow-hidden" style={{ height: 380 }}>
        <MapContainer
          center={NIGERIA_CENTER}
          zoom={DEFAULT_ZOOM}
          className="w-full h-full"
          zoomControl={false}
          dragging={false}
          scrollWheelZoom={false}
          doubleClickZoom={false}
        >
          <TileLayer
            key={theme}
            url={tile.url}
            attribution={tile.attribution}
            subdomains="abcd"
            maxZoom={20}
          />

          {markers.map(m => (
            <Marker
              key={m.id}
              position={[m.lat, m.lng]}
              icon={createStatusIcon(m.status, selected?.id === m.id)}
              eventHandlers={{ click: () => onSelect(m) }}
            />
          ))}

          <MapController selected={selected} />
          <MapInteractionController interactive={interactive} />
          <ZoomButtons />
          <MapThemeControls theme={theme} onThemeChange={handleThemeChange} />
        </MapContainer>

        {!hasCartoKey && theme !== 'satellite' && (
          <div className="absolute top-4 left-4 z-[1000] max-w-[260px] rounded-lg border border-amber-300 bg-amber-50/95 px-3 py-2 text-[11px] font-medium text-amber-800 shadow-sm">
            Map tiles need a free CARTO API key. Add it to REACT_APP_CARTO_API_KEY in .env.local — get one at carto.com/basemaps/apikey.
          </div>
        )}

        {/* Click-to-interact overlay */}
        {!interactive && (
          <div
            onClick={() => setInteractive(true)}
            className="absolute inset-0 rounded-2xl z-[1001] flex items-center justify-center cursor-pointer"
            style={{ background: 'rgba(5,14,30,0.55)', backdropFilter: 'blur(3px)' }}
          >
            <div
              className="flex flex-col items-center gap-3 px-8 py-6 rounded-2xl"
              style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.15)' }}
            >
              <svg width="34" height="34" viewBox="0 0 24 24" fill="none">
                <path d="M9 11V6a1.5 1.5 0 013 0v4.5" stroke="white" strokeWidth="1.8" strokeLinecap="round" />
                <path d="M12 10.5V9a1.5 1.5 0 013 0v1.5" stroke="white" strokeWidth="1.8" strokeLinecap="round" />
                <path d="M15 10.5a1.5 1.5 0 013 0v1" stroke="white" strokeWidth="1.8" strokeLinecap="round" />
                <path d="M9 11v4c0 2.5 2 4 4.5 4s4.5-1.5 4.5-4v-4" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <p className="text-white font-bold text-sm tracking-wide">Click to interact with map</p>
              <p className="text-white/50 text-xs">Pan, zoom, and select facilities</p>
            </div>
          </div>
        )}

        {/* Status legend */}
        <div className="absolute bottom-3 left-3 z-[999] bg-black/65 rounded-xl p-2.5 text-[10px] text-white space-y-1">
          <p className="font-bold mb-1.5 uppercase tracking-wide text-[9px]">Status</p>
          {Object.entries(MARKER_COLORS).map(([label, color]) => (
            <div key={label} className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: color }} />
              <span>{getFunctionalStatusMeta(label).label}</span>
            </div>
          ))}
        </div>

        <style>{`
          .leaflet-container { font-family: inherit; }
        `}</style>
      </div>

      {/*  Detail panel  */}
      <div className="w-full lg:w-56 bg-gray-50 border border-gray-200 rounded-xl p-4 flex flex-col items-center justify-center text-center">
        {selected ? (
          <>
            <div className="w-9 h-9 rounded-full mb-3 flex items-center justify-center"
              style={{ background: MARKER_COLORS[normalizeFunctionalStatus(selected.status)] ?? '#9ca3af' }}>
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <p className="font-bold text-sm text-gray-900">{selected.name}</p>
            <p className="text-xs mt-1 font-medium" style={{ color: MARKER_COLORS[normalizeFunctionalStatus(selected.status)] ?? '#6b7280' }}>
              {getFunctionalStatusMeta(selected.status).label}
            </p>
            <button
              onClick={() => onSelect(null)}
              className="mt-4 text-xs text-gray-400 hover:text-gray-600 underline underline-offset-2"
            >
              Clear selection
            </button>
          </>
        ) : (
          <>
            <svg className="w-8 h-8 text-gray-300 mb-2" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <p className="text-xs text-gray-400 leading-relaxed">Click a marker on the<br />map to view details</p>
          </>
        )}
      </div>
    </div>
  );
}
